import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import PropTypes from 'prop-types';
import { useEffect } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import I18n from 'I18n';
import UIModal from 'UIComponents/dialog/UIModal';
import UIDialogCloseButton from 'UIComponents/dialog/UIDialogCloseButton';
import UIDialogBody from 'UIComponents/dialog/UIDialogBody';
import UIDialogFooter from 'UIComponents/dialog/UIDialogFooter';
import UIButton from 'UIComponents/button/UIButton';
import { track } from 'ui-addon-upgrades/_core/common/eventTracking/tracker';
import { requestSeatUpgrade } from '../../upgrade/requestSeatUpgrade';
import { requestAdditionalSeats } from '../../upgrade/requestAdditionalSeats';
import { requestSKU } from '../../upgrade/requestSKU';
import { requestTrial } from '../../upgrade/requestTrial';
import { requestQuotePurchase } from '../../upgrade/requestQuotePurchase';
import * as AdminModalTypes from 'ui-addon-upgrades/_core/common/constants/AdminModalTypes';
import H2 from 'UIComponents/elements/headings/H2';
import UIDialogHeader from 'UIComponents/dialog/UIDialogHeader';
import FloatingAlertStore from 'UIComponents/alert/FloatingAlertStore';
import { getProductNameTextOrDefault, getProductNameTextFromApiNameOrDefault } from '../adapters/getProductNameText';
import { productLineToHub } from 'self-service-api/constants/ProductLineToHub';
import * as ProductLines from 'self-service-api/constants/ProductLines';
import { getUpgradeDataTrackingProperties } from 'ui-addon-upgrades/ums/utils'; // @ts-expect-error dependency missing types

import UpgradeProductToApiNameMap from 'self-service-api/constants/UpgradeProductToApiNameMap';
export const REQUEST_FUNCTION_BY_TYPE = {
  [AdminModalTypes.REQUEST_ADDITIONAL_SEATS]: requestAdditionalSeats,
  [AdminModalTypes.REQUEST_SEAT_UPGRADE]: requestSeatUpgrade,
  [AdminModalTypes.REQUEST_SEAT_UPGRADE_BANNER]: requestSeatUpgrade,
  [AdminModalTypes.REQUEST_SKU]: requestSKU,
  [AdminModalTypes.REQUEST_TRIAL]: requestTrial,
  [AdminModalTypes.REQUEST_QUOTE]: requestQuotePurchase
};

const AdminModal = ({
  modalType,
  upgradeData,
  productLine,
  onClose,
  canViewNewSeats
}) => {
  const getUpgradeSource = ({
    app,
    screen,
    uniqueId
  }) => `${app}-${screen}-admin-modal-${uniqueId}`;

  const apiNameToUse = canViewNewSeats && upgradeData.apiName ? upgradeData.apiName : UpgradeProductToApiNameMap[upgradeData.upgradeProduct];
  useEffect(() => {
    track('viewUpgradeAdminPrompt', Object.assign({
      apiName: apiNameToUse,
      modalType,
      upgradeSource: upgradeData.source || getUpgradeSource(upgradeData)
    }, getUpgradeDataTrackingProperties(upgradeData), {
      trialId: upgradeData.trialId
    })); // keep empty dependency array so that this event only fires once, not on every rerender
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleError = () => {
    FloatingAlertStore.addAlert({
      message: I18n.text('upgrades.adminModal.alertNotification.error.message'),
      titleText: I18n.text('upgrades.adminModal.alertNotification.error.titleText'),
      type: 'danger'
    });
  };

  const handleSuccess = () => {
    FloatingAlertStore.addAlert({
      message: I18n.text('upgrades.adminModal.alertNotification.success.message'),
      titleText: I18n.text('upgrades.adminModal.alertNotification.success.titleText'),
      type: 'success'
    });
  };

  const handleClick = () => {
    const hasRequestFunction = Object.keys(REQUEST_FUNCTION_BY_TYPE).includes(modalType);

    if (hasRequestFunction) {
      const args = modalType === AdminModalTypes.REQUEST_QUOTE && productLine ? {
        hub: productLineToHub[productLine]
      } : {
        apiName: apiNameToUse
      };

      if (REQUEST_FUNCTION_BY_TYPE[modalType]) {
        REQUEST_FUNCTION_BY_TYPE[modalType](args).then(handleSuccess).catch(handleError);
      }
    }

    onClose();
  };

  const productNameToUse = () => {
    return canViewNewSeats && upgradeData.apiName ? getProductNameTextFromApiNameOrDefault(upgradeData.apiName) : getProductNameTextOrDefault(upgradeData.upgradeProduct);
  };

  return /*#__PURE__*/_jsxs(UIModal, {
    size: "auto",
    width: 540,
    use: "success",
    "data-selenium": "admin-only-modal",
    "data-test-id": `admin-only-modal-${modalType}`,
    children: [/*#__PURE__*/_jsxs(UIDialogHeader, {
      children: [/*#__PURE__*/_jsx(UIDialogCloseButton, {
        onClick: () => onClose()
      }), /*#__PURE__*/_jsx(H2, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          "data-unit-test": "admin-modal__title",
          "data-test-id": "admin-modal__title",
          message: `upgrades.adminModal.${modalType}.title`
        })
      })]
    }), /*#__PURE__*/_jsx(UIDialogBody, {
      children: /*#__PURE__*/_jsx("p", {
        className: "p-right-4",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          "data-unit-test": "admin-modal__paragraph",
          "data-test-id": "admin-modal__paragraph",
          message: `upgrades.adminModal.${modalType}.paragraph`,
          options: {
            productName: productNameToUse()
          }
        })
      })
    }), /*#__PURE__*/_jsx(UIDialogFooter, {
      children: /*#__PURE__*/_jsx("div", {
        "data-selenium": "admin-modal-close-button",
        children: /*#__PURE__*/_jsx(UIButton, {
          className: "m-left-0",
          use: "primary",
          onClick: handleClick,
          "data-test-id": "admin-modal-submit-button",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            "data-unit-test": "admin-modal__button",
            "data-test-id": "admin-modal__button",
            message: `upgrades.adminModal.${modalType}.button`
          })
        })
      })
    })]
  });
};

AdminModal.propTypes = {
  apiName: PropTypes.string,
  modalType: PropTypes.oneOf(Object.values(AdminModalTypes || {})),
  onClose: PropTypes.func.isRequired,
  productLine: PropTypes.oneOf(Object.values(ProductLines)),
  upgradeData: PropTypes.object.isRequired
};
export default AdminModal;